import { FiscalVersionLine } from './FiscalVersionLine.js';
import { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { AuthGate, useAccessKind } from './auth/AuthGate.js';
import { Reservations } from './Reservations.js';
import { Calendrier } from './Calendrier.js';
import { Inventaire } from './Inventaire.js';
import { Tarifs } from './Tarifs.js';
import { PointageOta } from './PointageOta.js';
import { Rack } from './Rack.js';
import { Configuration } from './Configuration.js';
import { Occupation } from './Occupation.js';
import { Mirror } from './Mirror.js';
import { Preuve } from './Preuve.js';
import { Clients } from './Clients.js';
import { Encaissement } from './Encaissement.js';
import { Comptabilite } from './Comptabilite.js';
import { AdministrationFiscale } from './AdministrationFiscale.js';
import { PerEtablissement, PmsProvider } from './PmsProvider.js';
import { AppCapabilityProvider } from './AppCapabilityProvider.js';
import { AppBrandingProvider } from './AppBrandingProvider.js';
import { AccountMenu, DeskOperatorChip, useBranding, useCapability } from '@lemontaigu/platform-ui';
import { AppDeskOperatorProvider } from './AppDeskOperatorProvider.js';
import { CAPABILITIES } from '@lemontaigu/platform';
import { supabase } from './supabase.js';
// Generic shell utilities reused from reception (source export, not
// duplicated) — same PWA/nav plumbing as bos.* and reception.*.
import { NavLink } from '@lemontaigu/reception/nav/NavMenu.js';
import { hardReload, registerServiceWorker, type SwUpdate } from '@lemontaigu/reception/pwa/swUpdate.js';
import { InstallHint } from '@lemontaigu/reception/pwa/InstallHint.js';
// Reception's base (app shell + auth-*/app-nav/app-menu) first, PMS's own
// tab styles (source-exported, shared with the bos.* mount) second.
import '@lemontaigu/reception/styles/index.css';
// The board's date input (dd/mm/yyyy whatever the locale), used by the
// Encaissement sheets' day bar — reception.* and bos.* already load it.
import '@lemontaigu/reception/styles/fr-date.css';
import './styles/pms.css';

// PMS front desk (pms.lemontaigu.com) — approved employees (public.staff,
// password + TOTP + is_staff — RUNBOOK "admin-provisioned accounts", no
// public sign-up). Same shadow-mode P1 tabs as the bos.* PMS group; this app
// IS the source (bos.* mounts these same components via @lemontaigu/pms).

// ONE list, and the type is derived FROM it. Held the other way round until
// 2026-08-22, when « Tarifs » was added to the union and the nav and not to
// this array: the tab existed, its link set the hash, and `viewFromHash`
// rejected the value and bounced straight back to Réservations. A missing
// element in a `View[]` is perfectly legal TypeScript, so nothing caught it —
// only clicking the tab did. Derived, the omission is unrepresentable.
const VIEWS = ['reservations', 'plan', 'calendrier', 'occupation', 'clients', 'miroir', 'preuve', 'encaissement', 'comptabilite', 'fiscal', 'inventaire', 'tarifs', 'configuration', 'pointage'] as const;
type View = (typeof VIEWS)[number];
const viewFromHash = (): View => {
  const hash = window.location.hash.replace('#', '');
  return (VIEWS as readonly string[]).includes(hash) ? (hash as View) : 'reservations';
};

/**
 * The header — rendered as `PmsProvider`'s `chrome`, so it stays outside the
 * provider's loading gate and reads the tenant branding (docs/UI-BRANDING.md
 * §4.1, B1).
 *
 * Neither the app's name nor its colour is written here any more. « PMS » is a
 * CONSTANT — it is what this software is, and it does not vary by tenant;
 * « Le Montaigu » is DATA and arrives from `app_tenant.label_fr`. The blue is a
 * CSS custom property the branding provider sets. That split is the whole
 * point: the string this file used to hold was correct for exactly one
 * customer.
 */
function Nav({ view, onGo }: { view: View; onGo: (v: View) => void }) {
  const { label } = useBranding();
  // An inspector of the tax administration (P3 F4c-3) is offered « Administration fiscale » alone.
  const inspector = useAccessKind() === 'inspector';
  const canMirror = useCapability(CAPABILITIES.PMS_MIRROR_READ);
  // `pms.proof.read` is operator-only (admin wildcard, no `staff` row), like the mirror.
  const canProof = useCapability(CAPABILITIES.PMS_PROOF_READ);
  // Unlike `pms.mirror.read`, `pms.payment.read` IS granted to `staff`
  // (0186) — this gate matches the RLS grant rather than substituting an
  // operator-only one, so the tab shows for reception, exactly where the
  // desk needs it.
  const canPay = useCapability(CAPABILITIES.PMS_PAYMENT_READ);
  // `pms.guest.read` — the capability `pms_guest_search` raises without. Staff
  // hold it (0057), so reception sees the tab; the courtesy matches the gate.
  const canClients = useCapability(CAPABILITIES.PMS_GUEST_READ);
  // `fin.ledger.read` is admin-only (wildcard); the desk never sees the tab, and the reads refuse it anyway.
  const canLedger = useCapability(CAPABILITIES.FIN_LEDGER_READ);
  // `pms.fiscal.audit`: the operator (wildcard) and an inspector's time-limited login (P3 F4c).
  const canAudit = useCapability(CAPABILITIES.PMS_FISCAL_AUDIT);
  return (
    <nav className="app-nav">
      <span className="app-brand">{label('PMS')}</span>
      {inspector ? (
        <div className="app-groups">
          <NavLink label="Administration fiscale" accent="#5b7aa6" target="fiscal" view={view} onGo={onGo} />
        </div>
      ) : (
      <div className="app-groups">
        <NavLink label="Réservations" accent="#7a9e8e" target="reservations" view={view} onGo={onGo} />
        {/* « Planning » since 2026-09-14, and the hash stays `plan`. The label
            was « Plan » because that is the French word for the rack; the
            operator's point is that French borrowed « planning » back from
            English for exactly this object and every PMS on the market calls
            it that, so the desk reads « Planning » faster than it reads the
            correct French. The URL is not renamed with it: a hash is a
            bookmark and a PWA shortcut, and renaming one to match a label
            costs someone their saved link for no gain. */}
        <NavLink label="Planning" accent="#7a9e8e" target="plan" view={view} onGo={onGo} />
        <NavLink label="Calendrier" accent="#7a9e8e" target="calendrier" view={view} onGo={onGo} />
        <NavLink label="Occupation" accent="#7a9e8e" target="occupation" view={view} onGo={onGo} />
        {/* The cardex (PMS-CARDEX-BRIEF §6.1 S1). pms.* only: which tabs the
            reception.* and bos.* subsets carry is the operator's call. */}
        {canClients && <NavLink label="Clients" accent="#7a9e8e" target="clients" view={view} onGo={onGo} />}
        {/* Operator-only. `pms.mirror.read` has no grant row for `staff`, so
            hiding it here MATCHES the RLS rather than substituting for it —
            the policy is the gate, this is only the courtesy of not showing a
            tab that would come back empty (ARCHITECTURE §1: one gate per
            layer, none trusting the one above). */}
        {canMirror && (
          <NavLink label="Miroir Vega" accent="#8e7a9e" target="miroir" view={view} onGo={onGo} />
        )}
        {canProof && (
          <NavLink label="Preuve" accent="#8e7a9e" target="preuve" view={view} onGo={onGo} />
        )}
        {canPay && (
          <NavLink label="Encaissement" accent="#c98a4b" target="encaissement" view={view} onGo={onGo} />
        )}
        {canLedger && (
          <NavLink label="Comptabilité" accent="#5b7aa6" target="comptabilite" view={view} onGo={onGo} />
        )}
        {canAudit && (
          <NavLink label="Administration fiscale" accent="#5b7aa6" target="fiscal" view={view} onGo={onGo} />
        )}
        <NavLink label="Inventaire" accent="#7a9e8e" target="inventaire" view={view} onGo={onGo} />
        <NavLink label="Tarifs" accent="#7a9e8e" target="tarifs" view={view} onGo={onGo} />
        <NavLink label="Configuration" accent="#7a9e8e" target="configuration" view={view} onGo={onGo} />
        {/* CX2 (PMS-CHANNEX-CUTOVER-BRIEF F3): the extranet against the PMS. */}
        <NavLink label="Pointage OTA" accent="#7a9e8e" target="pointage" view={view} onGo={onGo} />
      </div>
      )}
      <button
        type="button"
        className="pwa-refresh-btn"
        title="Recharger l’application"
        aria-label="Recharger l’application"
        onClick={hardReload}
      >
        🔄
      </button>
      {/* No établissement list since U4 (PMS-UNIFIED-DESK-BRIEF §8): the
          screens show the whole reach, and a page about one établissement
          chooses inside the page. */}
      <DeskOperatorChip />
      <AccountMenu onSignOut={() => void supabase.auth.signOut()} />
    </nav>
  );
}

function App() {
  const [view, setView] = useState<View>(viewFromHash);
  const [swUpdate, setSwUpdate] = useState<SwUpdate | null>(null);

  useEffect(() => {
    const onHash = () => setView(viewFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    registerServiceWorker(setSwUpdate);
  }, []);

  const go = (v: View) => {
    window.location.hash = v;
    setView(v);
  };

  return (
    <AuthGate>
      {swUpdate && (
        <div className="update-banner">
          <span>Nouvelle version disponible.</span>
          <button type="button" onClick={() => swUpdate.apply()}>
            Recharger
          </button>
        </div>
      )}
      <InstallHint />
      {/* The providers wrap the HEADER as well as the tabs — the nav reads the
          tenant (branding). It goes in as `chrome` rather than as a child so it
          stays outside PmsProvider's loading gate: the header must not blink
          while the property list loads. */}
      <AppBrandingProvider>
        <AppCapabilityProvider>
          <AppDeskOperatorProvider>
            <PmsProvider chrome={<Nav view={view} onGo={go} />}>
              <Views view={view} />
              <FiscalVersionLine />
            </PmsProvider>
          </AppDeskOperatorProvider>
        </AppCapabilityProvider>
      </AppBrandingProvider>
    </AuthGate>
  );
}

/** The tab bodies. Inside the gate, so it knows who signed in: an inspector sees « Administration fiscale »
 *  whatever the hash says (a bookmark from a staff session must not open the rack for them). */
function Views({ view }: { view: View }) {
  if (useAccessKind() === 'inspector') {
    return (
      <main className="app-main">
        <PerEtablissement>
          <AdministrationFiscale />
        </PerEtablissement>
      </main>
    );
  }
  return (
            <main className="app-main">
              {view === 'reservations' && <Reservations />}
              {view === 'plan' && <Rack />}
              {view === 'calendrier' && <PerEtablissement><Calendrier /></PerEtablissement>}
              {view === 'occupation' && <PerEtablissement><Occupation /></PerEtablissement>}
              {view === 'clients' && <Clients />}
              {view === 'miroir' && <PerEtablissement><Mirror /></PerEtablissement>}
              {view === 'preuve' && <PerEtablissement><Preuve /></PerEtablissement>}
              {view === 'encaissement' && <Encaissement />}
              {view === 'comptabilite' && <PerEtablissement><Comptabilite /></PerEtablissement>}
              {view === 'fiscal' && <PerEtablissement><AdministrationFiscale /></PerEtablissement>}
              {view === 'inventaire' && <PerEtablissement><Inventaire /></PerEtablissement>}
              {view === 'tarifs' && <PerEtablissement><Tarifs /></PerEtablissement>}
              {view === 'configuration' && <Configuration />}
              {view === 'pointage' && <PerEtablissement><PointageOta /></PerEtablissement>}
            </main>
  );
}

createRoot(document.getElementById('root')!).render(<App />);
